import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import {
  type NormalizedEntityWriteMeta,
  type NormalizedEntityWriteResult,
  writeNormalizedEntitySnapshot,
} from './normalized-entity-write.js';
import { type PublicUserEntity, upsertPublicUserEntity } from './public-user-entity.js';

const PRIVATE_ACCOUNT_PUBLIC_FIELDS = [
  'displayName',
  'handle',
  'avatarUrl',
  'avatarNsfw',
  'avatarPreferences',
  'heroUrl',
  'heroNsfw',
  'bio',
  'country',
] as const;

const PRIVATE_ACCOUNT_FIELDS = [
  'email',
  'emailVerified',
  'phoneNumber',
  'turnkeySubOrgId',
  'onboardingCompleted',
  'locationSource',
  'totalPnl',
  'realizedPnl',
  'unrealizedPnl',
  'totalVolume',
  'totalTrades',
  'marketsTraded',
  'interestTopics',
  'interestPlatforms',
  'giftsDisabled',
  'giftIndexingEnabled',
  'createdAt',
  'wallets',
  'connectedProviders',
  'sessions',
] as const;

export type PrivateAccountEntity = Partial<
  Record<(typeof PRIVATE_ACCOUNT_FIELDS)[number], unknown>
>;

export interface PrivateAccountWriteResult extends NormalizedEntityWriteResult {
  readonly accountId: string | null;
}

export interface PrivateAccountDiagnostic {
  readonly kind: 'scope-mismatch' | 'stale-write';
  readonly accountHash: string;
}

export function privateAccountScope(
  viewerScope: string,
  networkScope: string,
  accountId: string,
): string {
  const identityScope =
    viewerScope.length > 0 ? viewerScope : `auth:${hashDiagnosticEntity(accountId)}`;
  return hashKey([identityScope, networkScope || 'unbound']);
}

export function privateAccountEntityKey(scope: string, accountId: string): QueryKey {
  return ['accounts', 'viewer', scope, 'entity', accountId];
}

export function privateAccountSnapshot(input: Record<string, unknown>): PrivateAccountEntity {
  const snapshot: PrivateAccountEntity = {};
  for (const field of PRIVATE_ACCOUNT_FIELDS) {
    const value = input[field];
    if (value !== undefined) snapshot[field] = value;
  }
  return snapshot;
}

export function upsertPrivateAccountEntity(
  queryClient: QueryClient,
  viewerScope: string,
  networkScope: string,
  input: Record<string, unknown>,
  meta: NormalizedEntityWriteMeta,
): PrivateAccountWriteResult {
  const accountId = readAccountId(input);
  if (accountId === null) {
    recordDiagnostic('scope-mismatch', 'missing');
    return { status: 'stale', accountId: null };
  }
  const scope = privateAccountScope(viewerScope, networkScope, accountId);
  const result = writeNormalizedEntitySnapshot(
    queryClient,
    privateAccountEntityKey(scope, accountId),
    privateAccountSnapshot(input),
    meta,
  );
  upsertPublicUserEntity(queryClient, publicUserInput(input, accountId), {
    ...meta,
    aliases: [accountId],
  });
  if (result.status === 'stale') recordDiagnostic('stale-write', accountId);
  return { ...result, accountId };
}

export function materializePrivateAccount<T extends Record<string, unknown>>(
  embedded: T,
  account: PrivateAccountEntity | undefined,
  publicUser: PublicUserEntity | undefined,
): T {
  if (account === undefined && publicUser === undefined) return embedded;
  const result: Record<string, unknown> = { ...embedded };
  materializeOwnedFields(result, embedded, publicUser, PRIVATE_ACCOUNT_PUBLIC_FIELDS);
  materializeOwnedFields(result, embedded, account, PRIVATE_ACCOUNT_FIELDS);
  return result as T;
}

/** Keeps route caches as request projections; all account and public identity
 * fields are read from their respective canonical entities. */
export function stripPrivateAccountProjection<T extends Record<string, unknown>>(row: T): T {
  const projection: Record<string, unknown> = { ...row };
  maskOwnedFields(projection, PRIVATE_ACCOUNT_PUBLIC_FIELDS);
  maskOwnedFields(projection, PRIVATE_ACCOUNT_FIELDS);
  return projection as T;
}

function publicUserInput(
  input: Record<string, unknown>,
  accountId: string,
): Record<string, unknown> {
  return { userId: accountId, ...publicAccountSnapshot(input) };
}

function publicAccountSnapshot(
  input: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (input === undefined) return {};
  const snapshot: Record<string, unknown> = {};
  for (const field of PRIVATE_ACCOUNT_PUBLIC_FIELDS) {
    const value = field === 'heroNsfw' ? (input.heroNsfw ?? input.coverNsfw) : input[field];
    if (value !== undefined) snapshot[field] = value;
  }
  return snapshot;
}

function materializeOwnedFields(
  result: Record<string, unknown>,
  projection: Record<string, unknown>,
  canonical: Record<string, unknown> | undefined,
  fields: readonly string[],
): void {
  if (canonical === undefined) return;
  for (const field of fields) {
    if (Object.hasOwn(projection, field) && Object.hasOwn(canonical, field)) {
      result[field] = canonical[field];
    }
  }
}

function maskOwnedFields(projection: Record<string, unknown>, fields: readonly string[]): void {
  for (const field of fields) {
    if (Object.hasOwn(projection, field)) projection[field] = undefined;
  }
}

function readAccountId(input: Record<string, unknown>): string | null {
  const raw = input.id ?? input.userId;
  if (typeof raw !== 'string') return null;
  const accountId = raw.trim();
  return accountId.length === 0 ? null : accountId;
}

function recordDiagnostic(kind: PrivateAccountDiagnostic['kind'], accountId: string): void {
  emit('private-account:diagnostic', { kind, accountHash: hashDiagnosticEntity(accountId) });
}
